import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import { Send, Globe, Link2, Loader2 } from "lucide-react";
import { V } from "@/lib/vocab";

/* One link you can text to a company you met yesterday. It works whether or not the project is
   listed on the open board — nobody has to be invited, registered or onboarded first, because
   holding the link IS the permission. */
function ShareProject({ tenantId, proj, shareUrl }) {
  const [url, setUrl] = useState(shareUrl || "");
  const [to, setTo] = useState({ phone: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);

  const call = async (send) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/tenants/${tenantId}/field-services/projects/${proj.id}/share-link`, send ? to : {});
      setUrl(data.url);
      if (send) {
        toast.success((data.sent || []).length ? `Sent to ${data.sent.join(" and ")}`
          : "Add a mobile number or an email first");
        setTo({ phone: "", email: "", note: "" });
      } else {
        await copyText(data.url, "Link copied — text it to anyone");
      }
    } catch (e) { toast.error(errMsg(e, "Could not make the link")); }
    setBusy(false);
  };

  return (
    <div className="mt-3 rounded-xl border border-sand-200 bg-sand-50/60 p-3" data-testid="project-share">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Share this project</div>
        {!url && (
          <button data-testid="project-share-btn" disabled={busy} onClick={() => call(false)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            Make a link
          </button>
        )}
      </div>
      <p className="mt-1 text-xs leading-relaxed text-ink/50">
        Anyone holding this link can read the brief and send you a price — no invitation, no account.
        Your {V.lc("org")}'s name stays hidden until they tell us who they are.
      </p>
      {url && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input readOnly value={url} data-testid="project-share-url"
              className="min-w-0 flex-1 rounded-lg border border-sand-200 bg-white px-3 py-2 text-xs text-ink/60" />
            <button data-testid="project-share-copy" onClick={() => copyText(url, "Link copied")}
              className="rounded-lg bg-ink px-3 py-2 text-xs font-bold text-white">Copy</button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <input data-testid="project-share-phone" value={to.phone} placeholder="Text it to (mobile)"
              onChange={(e) => setTo({ ...to, phone: e.target.value })}
              className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-xs outline-none" />
            <input data-testid="project-share-email" value={to.email} placeholder="Or email it to"
              onChange={(e) => setTo({ ...to, email: e.target.value })}
              className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-xs outline-none" />
            <button data-testid="project-share-send" disabled={busy || !(to.phone.trim() || to.email.trim())}
              onClick={() => call(true)}
              className="rounded-lg bg-redline-500 px-3 py-2 text-xs font-bold text-white disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send it"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric" }) : "");
// A manager should never have to know what "coi" or "w9" means to read a screen.
const PAPER_WORDS = { coi: "insurance", w9: "a W-9", agreement: "a signed agreement" };

/* ── the bid room: who was asked, who can actually bid, questions, walk-throughs ───────── */
export default function BidRoom({ tenantId, detail, onChanged, mayManage }) {
  const proj = detail.project;
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});
  const [rescue, setRescue] = useState(false);
  const invited = detail.invited || [];
  const questions = proj.questions || [];
  const visits = proj.site_visits || [];
  const bids = (detail.proposals || []).filter((p) => p.status !== "withdrawn").length;
  const closed = !!proj.bids_due && proj.bids_due.slice(0, 10) < new Date().toISOString().slice(0, 10);
  const bidFirst = detail.bid_without_paperwork !== false;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="bid-room">
      <div className="rounded-2xl border border-sand-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Who was asked</div>
          {mayManage && (
            <div className="flex flex-wrap gap-1.5">
              {proj.status === "open" && (
                <button data-testid="project-nudge" disabled={busy}
                  onClick={() => run(async () => {
                    const { data } = await api.post(`/tenants/${tenantId}/field-services/projects/${proj.id}/nudge`, {});
                    toast.success(data.notified ? `Reminded ${data.notified} compan${data.notified === 1 ? "y" : "ies"}`
                      : "Everyone invited has already bid");
                  })}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30 disabled:opacity-50">
                  <Send className="h-3.5 w-3.5" /> Remind who hasn't bid
                </button>
              )}
              <button data-testid="project-publish" disabled={busy}
                onClick={() => run(async () => {
                  const { data } = await api.post(`/tenants/${tenantId}/field-services/projects/${proj.id}/publish`,
                    { open_to_all: !proj.open_to_all });
                  toast.success(data.open_to_all
                    ? `On the open board${data.alerted ? ` · ${data.alerted} compan${data.alerted === 1 ? "y" : "ies"} alerted` : ""}`
                    : "Taken off the open board");
                })}
                className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30 disabled:opacity-50">
                <Globe className="h-3.5 w-3.5" /> {proj.open_to_all ? "Take off the open board" : "List on the open board"}
              </button>
            </div>
          )}
        </div>

        {proj.sole_source_reason && (
          <div data-testid="project-sole-source" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
            <b>Sole source.</b> Only one company was asked — {proj.sole_source_reason}
          </div>
        )}
        {detail.public_url && (
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="project-public-link">
            <span className="text-[11px] font-bold uppercase tracking-wider text-ink/40">On the open board</span>
            <input readOnly value={detail.public_url}
              className="min-w-0 flex-1 rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-xs text-ink/60" />
            <button onClick={() => copyText(detail.public_url, "Link copied — send it to anyone")}
              className="rounded-lg bg-ink px-3 py-2 text-xs font-bold text-white">Copy</button>
          </div>
        )}
        {mayManage && proj.status === "open" && (
          <ShareProject tenantId={tenantId} proj={proj} shareUrl={detail.share_url} />
        )}

        {!invited.length ? (
          <p className="mt-2 text-sm text-ink/45">
            {proj.open_to_all ? "Open to anyone who finds the listing — nobody invited directly."
              : "Nobody was invited directly."}
          </p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {invited.map((v) => (
              <div key={v.id} data-testid={`invited-${v.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sand-50 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink">{v.name}</div>
                  <div className="text-xs text-ink/45">
                    {v.bid ? "bid in" : v.viewed ? "opened it, no price yet" : "hasn't opened it"}
                    {v.network_status === "pending" ? " · still in Redline review" : ""}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                  v.bid ? "bg-emerald-50 text-emerald-700"
                    : v.paperwork_ok ? "bg-sand-200 text-ink/55" : "bg-amber-50 text-amber-800"}`}>
                  {v.bid ? "bid in" : v.paperwork_ok ? "can bid"
                    : `${bidFirst ? "owes" : "needs"} ${(v.missing || []).map((m) => PAPER_WORDS[m] || m).join(", ")}`}
                </span>
              </div>
            ))}
            <p className="text-xs leading-relaxed text-ink/45">
              {bidFirst
                ? "A company showing \"owes a W-9\" can still price the job — that's deliberate, it's how you get a number out of somebody you met last week. What the paperwork buys is the right to be handed the work."
                : `A company showing "needs a W-9" isn't uninterested — they're blocked. Insurance, a W-9 and a signed agreement are what buys the right to put a price in front of your ${V.grp()}.`}
            </p>
          </div>
        )}
      </div>

      {/* Site visits — no paperwork needed to come and look. */}
      <div className="rounded-2xl border border-sand-200 bg-white p-4" data-testid="site-visits">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Walk-throughs</div>
        {!visits.length ? (
          <p className="mt-1.5 text-sm text-ink/45">
            Nobody has asked to walk the site yet. Companies can request one from their bid page —
            it needs no paperwork from them, which is deliberate.
          </p>
        ) : visits.map((s) => (
          <div key={s.id} data-testid={`site-visit-${s.id}`} className="mt-2 rounded-xl bg-sand-50 px-3 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold text-ink">{s.vendor_name}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                s.status === "confirmed" ? "bg-emerald-50 text-emerald-700"
                  : s.status === "done" ? "bg-sand-200 text-ink/55"
                    : s.status === "declined" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                {s.status === "requested" ? "wants a time" : s.status}
              </span>
            </div>
            <div className="mt-0.5 text-xs text-ink/55">
              {s.status === "confirmed" || s.status === "done"
                ? `${s.window}${s.access_note && s.access_note !== s.window ? ` · ${s.access_note}` : ""}`
                : `They offered: ${(s.proposed || []).join(" · ")}`}
              {s.note ? ` — "${s.note}"` : ""}
            </div>
            {mayManage && s.status === "requested" && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button data-testid={`sv-confirm-${s.id}`} disabled={busy} onClick={() => {
                  const w = window.prompt("When will you meet them?", (s.proposed || [])[0] || "");
                  if (!w) return;
                  const access = window.prompt("Anything they need to get in? (optional)") || "";
                  run(() => api.post(
                    `/tenants/${tenantId}/field-services/projects/${proj.id}/site-visits/${s.id}/confirm`,
                    { when: w, access_note: access }), "Confirmed — they've been told");
                }} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
                  Confirm a time
                </button>
                <button data-testid={`sv-decline-${s.id}`} disabled={busy} onClick={() => {
                  const reason = window.prompt("Why not? (optional)") ?? "";
                  run(() => api.post(
                    `/tenants/${tenantId}/field-services/projects/${proj.id}/site-visits/${s.id}/decline`,
                    { reason }), "They've been told");
                }} className="rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 disabled:opacity-50">
                  Can't do it
                </button>
              </div>
            )}
            {mayManage && s.status === "confirmed" && (
              <button data-testid={`sv-done-${s.id}`} disabled={busy}
                onClick={() => run(() => api.post(
                  `/tenants/${tenantId}/field-services/projects/${proj.id}/site-visits/${s.id}/done`, {}), "Logged")}
                className="mt-2 rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 disabled:opacity-50">
                They came out
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Questions — answered once, sent to everyone bidding. */}
      {questions.length > 0 && (
        <div className="rounded-2xl border border-sand-200 bg-white p-4" data-testid="bid-questions">
          <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Questions from bidders</div>
          <p className="mt-1 text-xs leading-relaxed text-ink/45">
            Your answer goes to every company bidding. A private answer is how a bid protest starts.
          </p>
          {questions.map((q) => (
            <div key={q.id} data-testid={`bid-q-${q.id}`} className="mt-2 rounded-xl bg-sand-50 px-3 py-2.5">
              <div className="text-sm font-semibold text-ink">{q.q}</div>
              <div className="text-[11px] text-ink/40">{q.vendor_name} · {fmt(q.asked_at)}</div>
              {q.a ? (
                <div className="mt-1.5 rounded-lg bg-white px-3 py-2 text-sm text-ink/75">{q.a}</div>
              ) : mayManage ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <input data-testid={`bid-a-input-${q.id}`} value={answers[q.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    placeholder="Answer it once — everyone bidding gets it"
                    className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
                  <button data-testid={`bid-a-send-${q.id}`} disabled={busy || !(answers[q.id] || "").trim()}
                    onClick={() => run(async () => {
                      const { data } = await api.post(
                        `/tenants/${tenantId}/field-services/projects/${proj.id}/questions/${q.id}/answer`,
                        { a: answers[q.id], broadcast: true });
                      toast.success(`Answered · ${data.notified} bidder(s) told`);
                      setAnswers({ ...answers, [q.id]: "" });
                    })}
                    className="rounded-lg bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Send</button>
                </div>
              ) : <div className="mt-1 text-xs text-ink/40">Not answered yet.</div>}
            </div>
          ))}
        </div>
      )}

      {/* Zero bids at the deadline — three ways out, not silence. */}
      {mayManage && closed && bids === 0 && proj.status === "open" && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="zero-bid-rescue">
          <div className="font-heading text-base font-bold text-amber-900">Nobody bid</div>
          <p className="mt-1 text-sm leading-relaxed text-amber-900/80">
            The deadline passed with nothing in it. That's usually timing, not disinterest — three ways out:
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="rescue-extend" disabled={busy} onClick={() => {
              const due = window.prompt("New deadline (YYYY-MM-DD)");
              if (!due) return;
              run(() => api.post(`/tenants/${tenantId}/field-services/projects/${proj.id}/rescue`,
                { action: "extend", bids_due: due }), "Extended — everyone invited was told again");
            }} className="rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Give it longer</button>
            <button data-testid="rescue-widen" disabled={busy} onClick={() => run(async () => {
              const { data } = await api.post(`/tenants/${tenantId}/field-services/projects/${proj.id}/rescue`, {
                action: "widen", rings: { network: { trades: proj.category ? [proj.category] : [], area_only: false }, open_market: true },
              });
              toast.success(`Now asking ${data.invited} compan${data.invited === 1 ? "y" : "ies"} and the open board`);
            })} className="rounded-xl border border-amber-400 bg-white px-4 py-2 text-sm font-bold text-amber-900 disabled:opacity-50">
              Ask more companies
            </button>
            <button data-testid="rescue-direct" disabled={busy} onClick={() => setRescue(!rescue)}
              className="rounded-xl border border-amber-400 bg-white px-4 py-2 text-sm font-bold text-amber-900">
              Go direct to one
            </button>
          </div>
          {rescue && (
            <div className="mt-3 space-y-2 rounded-xl bg-white p-3">
              <select data-testid="rescue-vendor" id="rescue-vendor"
                className="w-full rounded-lg border border-sand-300 bg-white px-3 py-2 text-sm outline-none">
                <option value="">Pick a company…</option>
                {invited.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <input data-testid="rescue-reason" id="rescue-reason" placeholder="Why only them? (goes on the record)"
                className="w-full rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
              <button data-testid="rescue-direct-go" disabled={busy} onClick={() => {
                const vendor_id = document.getElementById("rescue-vendor")?.value;
                const reason = document.getElementById("rescue-reason")?.value;
                if (!vendor_id || !reason) return toast.error("Pick the company and give the reason");
                run(() => api.post(`/tenants/${tenantId}/field-services/projects/${proj.id}/rescue`,
                  { action: "direct", vendor_id, reason }), "Asked them directly");
              }} className="rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Ask them</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
